import { Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useDeletePartyMutation } from "Api/Mutations/Party/useDeletePartyMutation";
import { usePartiesQueries } from "Api/Queries/Party/usePartiesQueries";
import { getPartyQueryKey } from "Api/Queries/Party/usePartyQuery";
import { BeneficialOwnerDetail } from "Components/Shared/BeneficialOwnerDetail";
import type { CardListItemType } from "Components/Shared/CardsList/CardsList";
import { ControlledCardsList } from "Components/Shared/CardsList/ControlledCardsList";
import { ControlledCardsListSkeleton } from "Components/Shared/CardsList/ControlledCardsListSkeleton";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { BeneficialOwnerForm } from "Components/Shared/Merchant/BeneficialOwners/BeneficialOwnerForm";
import type { BeneficialOwnerFormData } from "Components/Shared/Merchant/BeneficialOwners/schema";
import { AddressType, type PartyDto } from "Infrastructure/Api/Api";
import { addressDtoToValidationSchema } from "Schemas/addressSchemas";
import { Resources, useResource } from "Translations/Resources";
import { getFormattedAddress } from "Utils/AddressUtils";
import { useState } from "react";
import { toast } from "sonner";

const COMPONENT_RESOURCES = Resources.Components.Merchant.BeneficialOwners;

type Props = {
  relatedPartiesIDs: Array<string>;
  owningPartyID: string;
  isReadOnly?: boolean;
};

export const BeneficialOwnersContent = ({
  relatedPartiesIDs,
  owningPartyID,
  isReadOnly,
}: Props) => {
  const [selectedBeneficialOwner, setSelectedBeneficialOwner] =
    useState<Partial<PartyDto> | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);

  const { t } = useResource();
  const queryClient = useQueryClient();
  const { mutate: deleteParty, isPending: isDeletePending } =
    useDeletePartyMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getPartyQueryKey(owningPartyID),
        });
        toast.success(t(COMPONENT_RESOURCES.Form.AddSuccess));
      },
      onError: () => {
        toast.error(t(COMPONENT_RESOURCES.Form.AddError));
      },
    });

  const { data, isPending } = usePartiesQueries(relatedPartiesIDs);

  if (isPending) {
    return <ControlledCardsListSkeleton isReadOnly={isReadOnly} />;
  }

  if (!data) {
    return <ErrorText>{t(COMPONENT_RESOURCES.LoadingError)}</ErrorText>;
  }

  const listItems = data
    .map((owner) => {
      const { firstName, lastName, addresses, publicID } = owner;
      const address = addresses[0];

      if (!publicID) {
        return null;
      }

      return {
        id: publicID,
        title: (
          <Typography fontWeight="500" fontSize="1rem">
            {firstName} {lastName}
          </Typography>
        ),
        subtitle: address
          ? getFormattedAddress(address)
          : t(Resources.Common.Address.NoAddressAvailable),
        ...(isReadOnly || isDeletePending
          ? {
              onView: () => {
                setSelectedBeneficialOwner(owner);
                setIsViewOpen(true);
              },
            }
          : {
              onEdit: () => {
                setSelectedBeneficialOwner(owner);
                setIsUpdateOpen(true);
              },
              onDelete: () => {
                confirm(
                  t(COMPONENT_RESOURCES.Form.DeleteConfirmation, {
                    name: `${firstName} ${lastName}`,
                  }),
                ) && deleteParty(publicID);
              },
            }),
      };
    })
    .filter((item) => !!item) as Array<CardListItemType>; // ts build throws error without the explicit type casting

  const editFormBeneficialOwner: Partial<BeneficialOwnerFormData> =
    selectedBeneficialOwner
      ? {
          ...selectedBeneficialOwner,
          address: addressDtoToValidationSchema(
            selectedBeneficialOwner.addresses?.find(
              ({ type }) => type === AddressType.Permanent,
            ),
          ),
        }
      : {};

  return (
    <>
      <ControlledCardsList
        listItems={listItems}
        isReadOnly={isReadOnly}
        addButtonConfig={{
          onClick: () => {
            setSelectedBeneficialOwner(null);
            setIsUpdateOpen(true);
          },
          text: t(COMPONENT_RESOURCES.AddBeneficialOwner),
        }}
        noItemsText={t(COMPONENT_RESOURCES.NoBeneficialOwners)}
      />
      <ModalDialog
        isOpen={isUpdateOpen}
        onClose={() => setIsUpdateOpen(false)}
        title={t(COMPONENT_RESOURCES.NewBeneficialOwner)}
      >
        <BeneficialOwnerForm
          beneficialOwner={editFormBeneficialOwner}
          currentPhone={selectedBeneficialOwner?.phone}
          currentOtherNationalities={
            selectedBeneficialOwner?.otherNationalities
          }
          owningPartyID={owningPartyID}
          onAfterSubmit={(isSuccess) => {
            if (isSuccess) {
              setSelectedBeneficialOwner(null);
              setIsUpdateOpen(false);
            }
          }}
        />
      </ModalDialog>
      <ModalDialog
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title={t(COMPONENT_RESOURCES.BeneficialOwnerDetail)}
      >
        {selectedBeneficialOwner && (
          <BeneficialOwnerDetail beneficialOwner={selectedBeneficialOwner} />
        )}
      </ModalDialog>
    </>
  );
};
